import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useEffect, useState } from 'react';
import UserInfoForm from './components/FamilyInfoForm';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FamilyRequest } from '@/api/DTO/family/family.interface';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { YESNO } from '@/common/constants/common';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useGetFamilyDetailQuery,
	useUpdateEducationMutation,
	useUpdateServiceDetailsMutation,
} from '@/api/family-management/familyApi';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { useGetDicListQuery } from '@/api/common';
export default function FamilyEditEducationPage() {
	const { t } = useTranslation();
	const { familyId } = useParams();
	const navigate = useNavigate();
	const [updateEducation] = useUpdateEducationMutation();

	const { data: details } = useGetFamilyDetailQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: workDicList } = useGetDicListQuery(
		'family_member_work_status',
		{ refetchOnMountOrArgChange: true }
	);

	const { data: workStatusDicList } = useGetDicListQuery('work_status', {
		refetchOnMountOrArgChange: true,
	});

	const { data: educationDicList } = useGetDicListQuery('education', {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		familyMemberWorkStatus: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
		}
	}, [details]);

	const onSubmit: SubmitHandler<FamilyRequest> = async (data) => {
		const response = await updateEducation(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFamilySuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		navigate(`/family-management/family-management-list/detail/${familyId}`);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box>
					<Typography variant='h4'>
						{t('drawerListItems.editFamilyMember')}
					</Typography>
					<Card sx={{ marginTop: 3 }}>
						<FormProvider {...methods}>
							<form onSubmit={methods.handleSubmit(onSubmit)}>
								<CardContent sx={{ padding: '24px' }}>
									<Typography
										variant='h6'
										sx={{ fontWeight: 'bold' }}
										marginBottom={2}
									>
										{t('family.occupationEducation')}
									</Typography>
									<Grid
										container
										spacing={3}
										marginTop={0}
										marginBottom={3}
										alignItems='center'
									>
										<Grid size={6}>
											<AppDropDownMenu
												control={methods.control}
												name='familyMemberWorkStatus'
												label={t('member.workStatus') + '*'}
												options={(workDicList?.data || []).map(
													(item: { labelEn: any }) => {
														return {
															...item,
															name: item.labelEn,
														};
													}
												)}
											/>
										</Grid>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name='familyMemberOccupation'
												label={t('family.occupation')}
												options={(workStatusDicList?.data || []).map(
													(item: { labelEn: any }) => {
														return {
															...item,
															name: item.labelEn,
														};
													}
												)}
											/>
										</Grid>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='education'
												label={t('preAdmission.education')}
												options={(educationDicList?.data || []).map(
													(item: { labelEn: any }) => {
														return {
															...item,
															name: item.labelEn,
														};
													}
												)}
											/>
										</Grid>
									</Grid>
								</CardContent>
								<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
									<Button onClick={handleClose}>{t('button.cancel')}</Button>
									<Button
										type='submit'
										variant='contained'
										endIcon={<CheckCircleOutlineIcon />}
									>
										{t('button.complete')}
									</Button>
								</CardActions>
							</form>
						</FormProvider>
					</Card>
				</Box>
			</Box>
		</>
	);
}
